import { useEffect, useState } from "react";
import { Button } from "@component/button";
import { ArrowRightIcon, BookmarkIcon } from "lucide-react";
import { Candidate, CandidateExplanation, ShortlistedCandidateItem } from "@type/candidate";
import { searchGenerateCandidateExplanation } from "@service/job";
import { catchErrorLog } from "@util/sentry";
import { checkHRViewLimit } from "@service/employer";
import { Badge } from "./badge";
import { useUserProfileContext } from "@context/user_profile";
import { bookmarkingCandidate, removeBookmarkCandidate } from "@service/user";
import { useSnackbar } from "@hook/use-snackbar";
import { getCookie, setCookie } from "@util/cookie";
import { cn } from "@util/tailwind";
import DialogViewLimit from "@container/dialog/dialog-view-limit";
import SkillBadge from "@component/skill-badge";

interface Props {
    isSelected?: boolean;
    candidate: Candidate;
    hasSearched?: boolean;
    isLoadNextPage: boolean;
    searchQuery: string;
    onSelectedCandidate: (candidate: Candidate) => void;
    onReloadSavedCandidate: () => void;
    reloadSavedBookmark?: number;
}

export function CandidateCard({ isSelected, candidate, isLoadNextPage, hasSearched = false, searchQuery = "", onSelectedCandidate, onReloadSavedCandidate, reloadSavedBookmark = 0 }: Props) {
    const snackbar = useSnackbar();
    const [isLoadingUserInfo, currentUserInfo] = useUserProfileContext();
    const [isLoadingCandidateExplanation, setLoadingCandidateExplanation] = useState<boolean>(false);
    const [isOpenModalViewLimit, setOpenModalViewLimit] = useState<boolean>(false);
    const [candidateExplanations, setCandidateExplanation] = useState<CandidateExplanation[]>([]);
    const [isSaved, setIsSaved] = useState<boolean>(false);
    const [isShortlisted, setIsShortlisted] = useState<boolean>(false);

    const loadCandidateExplanation = async () => {
        try {
            if (isLoadNextPage) {
                setLoadingCandidateExplanation(true);
                const resp = await searchGenerateCandidateExplanation(searchQuery, [candidate.user.id])
                if (resp.data.code === 200) {
                    setCandidateExplanation(resp.data.data || [])
                }
            }
        } catch (error) {
            catchErrorLog(`failed to search generate candidate explanation ${error}`)
        } finally {
            setLoadingCandidateExplanation(false);
        }
    }

    useEffect(() => {
        if (hasSearched) {
            loadCandidateExplanation();
        }
    }, [hasSearched])

    useEffect(() => {
        if (!isLoadingUserInfo && currentUserInfo && candidate) {
            const savedCandidates = getCookie(`saved_candidate:${currentUserInfo?.id}`);
            if (savedCandidates) {
                const exists = savedCandidates.find((item: string) => item === candidate.user.id);
                setIsSaved(!!exists);
            } else {
                setIsSaved(false);
            }

            const shortlistedCandidates = getCookie(`shortlisted_candidate:${currentUserInfo?.id}`);
            if (shortlistedCandidates) {
                const exists = shortlistedCandidates[candidate.user.id as keyof ShortlistedCandidateItem];
                setIsShortlisted(!!exists);
            } else {
                setIsShortlisted(false);
            }
        }
    }, [isLoadingUserInfo, currentUserInfo, candidate, reloadSavedBookmark])


    const handleViewProfileClick = (): void => {
        checkHRViewLimit(candidate.user.id).then((resp) => {
            if (!resp.data.data.allowed) {
                setOpenModalViewLimit(true);
            } else {
                onSelectedCandidate(candidate);
            }
        }).catch((error) => {
            if (error.response.status === 403) {
                setOpenModalViewLimit(true);
            }
        })
    };

    const handleBookmark = (e: React.MouseEvent) => {
        e.stopPropagation();
        setIsSaved(!isSaved);

        if (!isSaved) {
            bookmarkingCandidate(candidate.user.id)
                .then((resp) => {
                    if (resp.status === 200) {
                        snackbar.showSuccess("Added to your Saved Candidates.");
                        setCookie(`saved_candidate:${currentUserInfo?.id}`, JSON.stringify(resp.data.data));
                    }
                }).catch((error) => {
                    catchErrorLog(`failed to bookmark candidate: ${error}`);
                    snackbar.showError("Failed to bookmark candidate.");
                }).finally(() => {
                    onReloadSavedCandidate()
                })
        } else {
            removeBookmarkCandidate(candidate.user.id)
                .then((resp) => {
                    if (resp.status === 200) {
                        snackbar.showSuccess("Removed from your Saved Candidates.");
                        setCookie(`saved_candidate:${currentUserInfo?.id}`, JSON.stringify(resp.data.data));
                    }
                }).catch((error) => {
                    catchErrorLog(`failed to remove bookmark candidate: ${error}`);
                    snackbar.showError("Failed to remove candidate from bookmarks.");
                }).finally(() => {
                    onReloadSavedCandidate()
                })
        }
    }

    const candidateExplanation = candidateExplanations.find(item => item.candidate_id === candidate.user.id)?.explanation || "";

    return (
        <>
            <div
                className={`bg-white outline ${isSelected ? "outline-blue-600 outline-2" : "outline-gray-200 outline-1"} hover:outline-2 hover:outline-blue-500 rounded-lg p-5 cursor-pointer hover:shadow-md transition-shadow duration-200`}
                onClick={handleViewProfileClick}
            >
                <div className="flex items-start">
                    <div className="flex-1">
                        <div className="flex justify-between items-start">
                            <div>

                                <div className="flex items-center gap-2">
                                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                                        {candidate.cv_data?.CV_data?.name || "—"} | Exp: {candidate.dynamic_yoe > 0 ? (candidate.dynamic_yoe > 1) ? `${candidate.dynamic_yoe} years` : "1 year" : "<1 year"}
                                    </h3>
                                    {["admin", "super_admin"].includes(currentUserInfo?.role || "") && candidate.byoc_company && (
                                        <Badge
                                            variant="secondary"
                                            className="text-xs px-2 py-0.5 bg-purple-50 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300 border border-purple-200 dark:border-purple-800"
                                            style={{
                                                paddingTop: "4px",
                                                paddingBottom: "4px",
                                                borderRadius: "1rem",
                                            }}
                                        >
                                            BYOC: {candidate.byoc_company || "N/A"}
                                        </Badge>
                                    )}
                                    {isShortlisted && (
                                        <span
                                            className="text-xs py-1 bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300 rounded-full font-medium"
                                            style={{ paddingLeft: "10px", paddingRight: "10px" }}
                                        >
                                            Favourited
                                        </span>
                                    )}
                                </div>
                                <p className="text-gray-600 dark:text-gray-400">
                                    {candidate.cv_data?.CV_data?.position || ""}
                                </p>
                            </div>
                            <div className="flex items-center gap-2">
                                {currentUserInfo?.permission !== "member" && (
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-8 w-8 p-0 border border-border hover:bg-blue-50 dark:hover:bg-blue-950 hover:border-blue-200 dark:hover:border-blue-800 transition-colors"
                                        onClick={handleBookmark}
                                    >
                                        <BookmarkIcon
                                            className={cn(
                                                "h-4 w-4 transition-colors",
                                                isSaved
                                                    ? "text-blue-600 dark:text-blue-400 fill-blue-600 dark:fill-blue-400"
                                                    : "text-gray-400 dark:text-gray-600 hover:text-blue-600 dark:hover:text-blue-400"
                                            )}
                                        />
                                    </Button>
                                )}

                                <Button
                                    size="sm"
                                    className="transition-all duration-300 overflow-hidden relative hidden sm:block"
                                    onClick={() => handleViewProfileClick()}
                                >
                                    <span>View Profile</span>
                                    <ArrowRightIcon
                                        className="h-4 w-4 transition-all duration-300 absolute group-hover:relative -right-10 opacity-0 group-hover:opacity-100 group-hover:right-0"
                                    />
                                </Button>
                            </div>
                        </div>
                        <p className="mt-2 text-sm text-gray-700 dark:text-gray-300">
                            {candidate.cv_data?.CV_data?.professional_summary || ""}
                        </p>
                        {/* AI Suggestion Section - Only show when search has been performed */}
                        {(isLoadNextPage && isLoadingCandidateExplanation && hasSearched) ? (
                            <div className="mt-3 border border-gray-200 dark:border-gray-700 rounded-md p-2">
                                <div className="flex items-start gap-2">
                                    <div className="flex-shrink-0 w-4 h-4 rounded-md bg-blue-100 dark:bg-blue-900 flex items-center justify-center overflow-hidden">
                                        <img
                                            src={`${import.meta.env.VITE_ASSET_OURTEAM_BASE_URL}/commons/ourteam_logo_only.png`}
                                            className="w-full"
                                        />
                                    </div>
                                    <div className="flex-1">
                                        <div className="animate-pulse space-y-2">
                                            <div className="h-2 bg-gray-200 rounded w-3/4"></div>
                                            <div className="h-2 bg-gray-200 rounded w-1/2"></div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <>
                                {((hasSearched && candidate.one_sentence_suitability && candidate.one_sentence_suitability !== "Could not complete analysis due to an error.") || candidateExplanation.length > 0) && (
                                    <div className="mt-3 border border-gray-200 dark:border-gray-700 rounded-md p-2">
                                        <div className="flex items-start gap-2">
                                            <div className="flex-shrink-0 w-4 h-4 rounded-md bg-blue-100 dark:bg-blue-900 flex items-center justify-center overflow-hidden">
                                                <img
                                                    src={`${import.meta.env.VITE_ASSET_OURTEAM_BASE_URL}/commons/ourteam_logo_only.png`}
                                                    className="w-full"
                                                />
                                            </div>
                                            <div className="flex-1">
                                                <p className="text-sm text-gray-700 dark:text-gray-300">
                                                    {candidate.one_sentence_suitability || candidateExplanation}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                        {candidate.cv_data.summary_data.relevant_tags.length > 0 && (
                            <div className="mt-3">
                                <p className="text-sm font-bold text-gray-700 dark:text-gray-300 mb-1.5">
                                    Expert in
                                </p>
                                <div className="flex flex-wrap gap-2">
                                    {candidate.cv_data.summary_data.relevant_tags.slice(0, 8).map((tag, index) => (
                                        <SkillBadge key={index} label={tag} />
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <DialogViewLimit
                open={isOpenModalViewLimit}
                onClose={setOpenModalViewLimit}
                title="You're on ourteam Preview"
                description={(
                    <p className="text-gray-500">
                        Browse up to 30 talent profiles and explore how ourteam works.
                        <br /><br />
                        To post a job or unlock full access, just email us at <b>hello@ourteam.ai</b>— we’ll get you started.
                    </p>
                )}
            />
        </>
    );
}
